import { H1, P } from "@/components/ui/Heading";
import { Separator } from "@/components/ui/Separator";
import clsx from "clsx";
import Image from "next/image";
import { ReactNode } from "react";

export interface ContentBlockProps {
  title: string | ReactNode;
  subTitle?: string;
  logo?: string;
  variant?: "shadow" | "border" | "normal" | "plain";
  children: ReactNode;
  buttons?: ReactNode;
  className?: string;
  icon?: React.ElementType;
  id?: string;
  size?: "sm" | "lg";
  titleElement?: React.ElementType;
}

export const ContentBlock = ({
  title,
  logo,
  variant = "normal",
  subTitle,
  children,
  buttons,
  icon: Icon,
  className,
  id,
  size = "lg",
  titleElement: TitleElement = "h1",
}: ContentBlockProps) => {
  const shadow = variant === "shadow";
  const border = variant === "border";

  return (
    <div
      className={clsx(
        `flex flex-col mx-6 sm:mx-10 lg:mx-22 overflow-hidden ${className}`,
        {
          "rounded-2xl shadow-sm md:rounded-2xl bg-white min-w-screen": shadow,
          "border rounded-2xl bg-white min-w-screen": border,
          "p-6": border || shadow,
          "gap-6 sm:gap-8": size === "lg",
          "gap-3 sm:gap-4": size === "sm",
        }
      )}
      {...(id ? { id } : {})}
    >
      <div
        className={clsx("inline-flex justify-between", {
          "items-center flex-col-reverse sm:flex-row sm:items-start":
            shadow && logo,
        })}
      >
        <div
          className={clsx("flex h-full items-center", {
            "gap-3": size === "sm",
            "gap-6": size === "lg",
          })}
        >
          {!shadow && !Icon && variant !== "plain" && (
            <Separator
              className="bg-primary w-[2px] self-stretch"
              orientation="vertical"
            />
          )}

          {!shadow && Icon && <Icon width={48} height={48} />}

          <div
            className={clsx("flex flex-col gap-2", {
              "text-center sm:text-start": shadow && logo,
            })}
          >
            <H1
              as={TitleElement}
              className={clsx({
                "text-[18px] font-semibold sm:font-normal": border,
                "!text-[22px] !leading-6 font-bold": size == "sm",
              })}
            >
              {title}
            </H1>
            {subTitle && <P className="text-sm sm:text-lg">{subTitle}</P>}
          </div>
        </div>

        {buttons && <div className="hidden sm:flex">{buttons}</div>}

        {logo && (
          <div className="relative h-20 w-32">
            <Image
              src={logo}
              fill
              className="!object-fit"
              alt={subTitle ?? ""}
              quality={100}
            />
          </div>
        )}
      </div>

      {border && <Separator />}
      {children}
      {buttons && (
        <div className="items-center flex sm:hidden w-full justify-center">
          {buttons}
        </div>
      )}
    </div>
  );
};
